import { Activity, PropsWithChildren, ReactNode, useState } from 'react';

import { styled } from '@mui/material';

import {
  Button,
  Icons,
  SxProps,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import type { TypographyProps } from '@cast/design-system';

const CaretUp = styled(Icons.CaretUp)({});
const CaretDown = styled(Icons.CaretDown)({});

type ExpandablePanelProps = PropsWithChildren<{
  title: string;
  description?: ReactNode;
  descriptionProps?: TypographyProps;
  caretPosition?: 'start' | 'end';
  defaultIsExpanded?: boolean;
  testId?: string;
  buttonSx?: SxProps;
  containerSx?: SxProps;
  typography?: TypographyProps['variant'];
  sx?: SxProps;
}>;

export const ExpandablePanel = ({
  title,
  description,
  descriptionProps,
  defaultIsExpanded,
  caretPosition = 'start',
  children,
  testId,
  buttonSx,
  containerSx,
  typography,
  sx,
}: ExpandablePanelProps) => {
  const [isOpen, setIsOpen] = useState(defaultIsExpanded);

  const Caret = isOpen ? CaretUp : CaretDown;

  return (
    <Stack sx={containerSx}>
      <Stack direction="row" width="100%" sx={sx} typography={typography}>
        <Button
          variant="text"
          onClick={() => setIsOpen(!isOpen)}
          startIcon={caretPosition === 'start' && <Caret />}
          endIcon={caretPosition === 'end' && <Caret />}
          sx={{
            '--ds-button-iconGap': '0.5em',
            justifyContent: 'flex-start',
            borderRadius: 0,
            typography,
            ...buttonSx,
          }}
          testId={testId}
        >
          {title}
        </Button>
        {description ? (
          <Typography
            component="span"
            variant="inherit"
            color={tokens.theme.fg.structural.secondary}
            ml={8}
            {...descriptionProps}
          >
            {description}
          </Typography>
        ) : null}
      </Stack>
      <Activity mode={isOpen ? 'visible' : 'hidden'}>{children}</Activity>
    </Stack>
  );
};
